import React, { useState } from "react";
import { Column, Table } from "@120wateraudit/waterworks";
import { DownloadIcon } from "src/components/Icons/Download";
import Loader from "src/components/Loader";
import { ExportType } from "src/types/enums/ExportRequest";
import { formatDateTime } from "src/utils/format";
import ExportStatusBadge from "./ExportStatusBadge";
import { Export, useGetAllExportsQuery, useLazyDownloadExportQuery } from "src/services/export-service-api";
import { useTableState } from "src/hooks/useTableState";

interface CenteredCellProps {
  children: React.ReactNode;
  header?: boolean;
}

const CenteredCell: React.FC<CenteredCellProps> = ({ children, header }) => (
  <div style={{ textAlign: "center" }}>
    {header ? <b>{children}</b> : children}
  </div>
);

const getExportTypeString = (exportType: ExportType): string => {
  switch (exportType) {
    case ExportType.FullSDWIS:
      return 'SDWIS - All'
    case ExportType.MostRecentSDWIS:
      return 'SDWIS - New'
    case ExportType.PWSSubmissionPeriod:
      return 'Submissions'
    case ExportType.StateUtilities:
      return 'Utilities'
    case ExportType.SubmissionDetails:
      return 'Submission Detail'
    case ExportType.SubmissionQuestions:
    case ExportType.SubmissionQuestionsAZ:
    case ExportType.SubmissionQuestionsDE:
    case ExportType.SubmissionQuestionsGA:
    case ExportType.SubmissionQuestionsIN:
    case ExportType.SubmissionQuestionsOR:
    case ExportType.SubmissionQuestionsRI:
      return 'Methodology Questions'
    case ExportType.CustomerNoticeResponses:
      return 'Customer Notice Responses'
  }
}

const ExportLog: React.FC = () => {
  const [downloadExport] = useLazyDownloadExportQuery();
  const [downloadState, setDownloadState] = useState(false);

  const {
    params,
    setFilters,
    setPage,
    setPageSize,
  } = useTableState()

  const {
    data: exports,
    isFetching,
    isLoading
  } = useGetAllExportsQuery(
    {
      searchParams: params
    },
    {
      pollingInterval: 15 * 1000,
      refetchOnMountOrArgChange: true
    }
  )

  const handleDownload = async (
    id: number,
    name: string,
    setDownloadState: any
  ): Promise<void> => {
    setDownloadState(true);
    try {
      await downloadExport({ id, name })
    } catch (error) {
      console.error(`Error downloading export with id ${id}:`, error);
    } finally {
      setDownloadState(false);
    }
  };

  const COLUMNS: Array<Column<Export>> = [
    {
      Header: <CenteredCell header>Export ID</CenteredCell>,
      name: "Export ID",
      key: "id",
      accessor: (e: Export) => <CenteredCell>{e.id}</CenteredCell>,
    },
    {
      Header: <CenteredCell header>File Name</CenteredCell>,
      name: "File Name",
      key: "fileName",
      accessor: (e: Export) => <CenteredCell>{e.fileName ?? 'N/A'}</CenteredCell>,
    },
    {
      Header: <CenteredCell header>Submission Period</CenteredCell>,
      name: "Submission Period",
      key: "submissionPeriod",
      accessor: (e: Export) => (
        <CenteredCell>{e.metaData?.submissionPeriodName ?? 'N/A'}</CenteredCell>
      ),
    },
    {
      Header: <CenteredCell header>Type</CenteredCell>,
      name: "Type",
      key: "exportType",
      accessor: (e: Export) => {
        return (
          <CenteredCell>
            {getExportTypeString(e.type)}
          </CenteredCell>
        );
      },
    },
    {
      Header: <CenteredCell header># of Records</CenteredCell>,
      name: "# of Records",
      key: "rowCount",
      accessor: (e: Export) => <CenteredCell>{e.rowCount ?? 'N/A'}</CenteredCell>,
    },
    {
      Header: <CenteredCell header>Exporter</CenteredCell>,
      name: "Exporter",
      key: "exporter",
      accessor: (e: Export) => <CenteredCell>{e.metaData?.createdByEmail ?? e.createdByUsername}</CenteredCell>,
    },
    {
      Header: <CenteredCell header>Started At</CenteredCell>,
      name: "Started At",
      key: "createdOn",
      accessor: (e: Export) => (
        <CenteredCell>{formatDateTime(e.createdOn)}</CenteredCell>
      ),
    },
    {
      Header: <CenteredCell header>Status</CenteredCell>,
      name: "Status",
      key: "status",
      accessor: (e: Export) => (
        <CenteredCell>
          <ExportStatusBadge status={e.status} errors={e.errors} />
        </CenteredCell>
      ),
    },
    {
      Header: <CenteredCell header>Download</CenteredCell>,
      name: "Download",
      key: "download",
      accessor: (e: Export) => {
        if (!e.fileName || e.fileName === 'N/A') return

        return (
          <CenteredCell>
            <div
              onClick={(p) => {
                p.preventDefault();
                void handleDownload(e.id, e.fileName, setDownloadState);
              }}
              style={{ cursor: "pointer" }}
            >
              {downloadState ? <Loader /> : <DownloadIcon />}
            </div>
          </CenteredCell>
        );
      },
    },
  ];

  return (
    <div>
      <h2>Export Log</h2>
      <Table
          data={exports?.data ?? []}
          columns={COLUMNS}
          onFilterChanged={setFilters}
          loading={isLoading || isFetching}
          onPageChanged={setPage}
          onPageSizeChanged={setPageSize}
          page={params.page}
          pageSize={params.pageSize}
          totalRecords={exports?.count ?? 0}
          maxHeight={"57vh"}
          stickyFirstColumn
          stickyHeader
          paginated />
    </div>
  );
};

export default ExportLog;
